import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { BiDashboardSummary } from '@euler/model/bi/dashboard';
import { CirclePlus, Database, FileDown, Sparkles } from 'lucide-react-native';
import { memo, useState } from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BiNamePanel } from '../components/BiNamePanel';
import { BiButton } from '../components/ui/BiButton';
import {
  BiPageHeader,
  kBiContentMaxWidth,
} from '../components/ui/BiPageHeader';
import { BiRetry } from '../components/ui/BiRetry';
import { biErrorMessage, isBiRevisionConflict } from '../functions/biErrors';
import { DashboardListEmpty } from './DashboardListEmpty';
import { DashboardListSection } from './DashboardListSection';
import { useBiDashboardList } from './functions/useBiDashboardList';
import { useBiDefaultSet } from './functions/useBiDefaultSet';

type NamePrompt =
  | { kind: 'create' }
  | { kind: 'rename'; item: BiDashboardSummary };

/** The store's dashboards: mine first, then those shared with me. */
export const BiDashboardListView = memo(
  ({
    mobile,
    onBack,
    onOpen,
    onAskAi,
    onOpenLibrary,
    onOpenExports,
  }: {
    mobile: boolean;
    onBack: () => void;
    onOpen: (dashboardId: string) => void;
    onAskAi: () => void;
    /** The data library: uploads, shared and built-in data. */
    onOpenLibrary: () => void;
    /** The member's PDF exports. */
    onOpenExports: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const confirm = useConfirm();
    const { state, sections, refresh, create, rename, remove } =
      useBiDashboardList();
    const defaultSet = useBiDefaultSet();
    const [width, setWidth] = useState(0);
    const [prompt, setPrompt] = useState<NamePrompt | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const openPrompt = (next: NamePrompt) => {
      setError(null);
      setPrompt(next);
    };
    const submitName = async (name: string) => {
      if (!prompt) return;
      setBusy(true);
      setError(null);
      try {
        if (prompt.kind === 'create') {
          const dashboard = await create(name);
          setPrompt(null);
          onOpen(dashboard.id);
        } else {
          await rename(prompt.item, name);
          setPrompt(null);
        }
      } catch (e) {
        console.error('BI dashboard name failed', e);
        if (isBiRevisionConflict(e)) refresh();
        setError(
          isBiRevisionConflict(e)
            ? '看板刚被修改过，已刷新，请再试一次。'
            : biErrorMessage(e, '没有保存成功，请稍后重试。'),
        );
      } finally {
        setBusy(false);
      }
    };
    const deleteItem = async (item: BiDashboardSummary) => {
      const ok = await confirm({
        title: '删除看板',
        message: `删除「${item.name}」后，看板上的 ${item.cardCount} 张图表也会一起删除，共享给的成员将无法再查看。`,
        confirmButtonText: '删除',
        destructive: true,
      });
      if (!ok) return;
      try {
        await remove(item);
      } catch (e) {
        console.error('BI dashboard delete failed', e);
        refresh();
      }
    };

    return (
      <View style={{ flex: 1, backgroundColor: aiColors.background }}>
        <BiPageHeader
          title="数据看板"
          onBack={onBack}
          actions={
            <>
              <BiButton
                icon={Database}
                title={mobile ? undefined : '我的数据'}
                accessibilityLabel="我的数据"
                onPress={onOpenLibrary}
              />
              <BiButton
                icon={FileDown}
                title={mobile ? undefined : '导出记录'}
                accessibilityLabel="导出记录"
                onPress={onOpenExports}
              />
              <BiButton
                icon={Sparkles}
                title={mobile ? undefined : '问 AI'}
                accessibilityLabel="问 AI"
                onPress={onAskAi}
              />
              <BiButton
                icon={CirclePlus}
                title="新建看板"
                tone="primary"
                onPress={() => openPrompt({ kind: 'create' })}
              />
            </>
          }
        />
        {!state.items ? (
          <View
            style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
          >
            {state.failed ? (
              <BiRetry onRetry={refresh} />
            ) : (
              <ActivityIndicator color={aiColors.primary} />
            )}
          </View>
        ) : (
          <ScrollView
            contentContainerStyle={{
              padding: rpx(16),
              paddingBottom: insets.bottom + rpx(32),
            }}
          >
            <View
              onLayout={event => setWidth(event.nativeEvent.layout.width)}
              style={{
                width: '100%',
                maxWidth: kBiContentMaxWidth,
                alignSelf: 'center',
                gap: rpx(24),
              }}
            >
              {!state.items.length ? (
                <DashboardListEmpty
                  recommended={defaultSet.state}
                  onUseRecommended={() => {
                    void defaultSet.create().then(id => {
                      if (id) onOpen(id);
                    });
                  }}
                  onAskAi={onAskAi}
                  onCreate={() => openPrompt({ kind: 'create' })}
                />
              ) : width ? (
                <>
                  <DashboardListSection
                    title="我的看板"
                    empty="还没有看板。新建一个，或在 AI 对话里把图表加到看板。"
                    items={sections.mine}
                    width={width}
                    onOpen={item => onOpen(item.id)}
                    onRename={item => openPrompt({ kind: 'rename', item })}
                    onDelete={deleteItem}
                  />
                  {sections.staff.length ? (
                    <DashboardListSection
                      title="员工看板"
                      empty=""
                      items={sections.staff}
                      width={width}
                      onOpen={item => onOpen(item.id)}
                      onRename={item => openPrompt({ kind: 'rename', item })}
                      onDelete={deleteItem}
                    />
                  ) : null}
                  <DashboardListSection
                    title="共享给我"
                    empty="还没有同事共享看板给你。"
                    items={sections.shared}
                    width={width}
                    onOpen={item => onOpen(item.id)}
                    onRename={() => undefined}
                    onDelete={() => undefined}
                  />
                </>
              ) : null}
            </View>
          </ScrollView>
        )}
        <BiNamePanel
          visible={prompt !== null}
          title={prompt?.kind === 'rename' ? '重命名看板' : '新建看板'}
          initialName={prompt?.kind === 'rename' ? prompt.item.name : ''}
          confirmTitle={prompt?.kind === 'rename' ? '保存' : '创建'}
          error={error}
          busy={busy}
          onConfirm={submitName}
          onClose={() => setPrompt(null)}
        />
      </View>
    );
  },
);
